import React, { FormEvent, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import styles from './TokenizationConfirmationForm.module.scss';
import { ReactComponent as PaymentMethodImage } from './paymentMethod.svg';
import TextField from 'components/common/TextField';
import Button from 'components/common/Button';
import { UseFormReturn } from 'react-hook-form';
import AlertBadge from 'components/common/AlertBadge';

export type TokenizationConfirmationFormData = {
    confirmationAmount: string;
};

export type TokenizationConfirmationFormProps = {
    isOpen?: boolean;
    countdown?: string;
    form?: UseFormReturn<TokenizationConfirmationFormData>;
    isLoading?: boolean;
    onClose?: () => void;
    onSubmit?: (event: FormEvent) => void;
    attemps: number;
    error: boolean;
    errorMessage: string | null;
};

export const TokenizationConfirmationForm: React.FC<TokenizationConfirmationFormProps> = (
    props,
) => {
    const [container] = useState(() => document.createElement('div'));
    const root = document.body;
    const regex = /^\d+$/;
    const patternNumber = regex.source;

    const confirmationAmount = props.form?.watch('confirmationAmount');

    useEffect(() => {
        props.form?.setValue('confirmationAmount', confirmationAmount?.replace(/\D/g, '') ?? '');
    }, [props.form, confirmationAmount]);

    useEffect(() => {
        root.appendChild(container);
        return () => {
            root.removeChild(container);
        };
    }, [container, root]);

    const component = (
        <div className={styles.wrapper}>
            <div className={`${styles.modal}`} data-testid="modal">
                <div className={styles.header}>
                    <div className={styles.title}>Cargo tarjeta</div>
                    <button
                        onClick={props.onClose}
                        disabled={props.isLoading}
                        data-testid="close-button"
                    />
                </div>
                <div className={styles.body}>
                    <div className={styles.container}>
                        <div className={styles.content}>
                            <div className={styles.image}>
                                <PaymentMethodImage />
                            </div>
                            <div className={styles.firstText}>
                                Ingresa el monto cargado en tu método de pago
                            </div>
                            <div className={styles.secondText}>
                                Verifica el monto que cargamos en tu tarjeta como método de
                                validación
                            </div>
                            <div
                                className={styles.countdown}
                                style={{ display: props.error ? 'none' : '' }}
                            >
                                Tienes {props.countdown} minutos para validar tu pago
                            </div>
                            <div
                                className={styles.countdown}
                                style={{ display: props.error ? 'none' : '' }}
                            >
                                Te quedan {props.attemps} intentos.
                            </div>
                            <form
                                onSubmit={props.onSubmit}
                                data-testid="tokenization-confirmation-form"
                            >
                                <div
                                    className={styles.validateAmount}
                                    data-testid="tokenization-confirmation-input"
                                >
                                    <TextField
                                        label={'Monto cargado'}
                                        maxLength={5}
                                        inputMode={'numeric'}
                                        pattern={patternNumber}
                                        disabled={props.isLoading || props.error}
                                        placeholder="$"
                                        {...props.form?.register('confirmationAmount')}
                                    />
                                </div>
                            </form>
                            {props.error && (
                                <div
                                    className={styles['error']}
                                    style={{ marginBottom: 15, marginTop: -10 }}
                                >
                                    {props.errorMessage &&
                                    props.errorMessage === 'Debit Card is invalid' ? (
                                        <AlertBadge theme="danger" size="medium">
                                            No se pueden utilizar tarjetas de débito en este medio
                                            de pago.
                                        </AlertBadge>
                                    ) : (
                                        <AlertBadge theme="danger" size="medium">
                                            Ha ocurrido un problema al guardar los datos de tu
                                            tarjeta. Por favor, inténtalo nuevamente.
                                        </AlertBadge>
                                    )}
                                </div>
                            )}
                            <div className={styles.sendButton}>
                                <Button
                                    theme={'primary'}
                                    size={'large'}
                                    onClick={props.onSubmit}
                                    disabled={props.isLoading || props.error}
                                    loading={props.isLoading}
                                >
                                    Ingresar monto
                                </Button>
                            </div>
                            <div data-testid="tokenization-confirmation-cancel-button">
                                <Button
                                    theme={'no-border'}
                                    size={'large'}
                                    onClick={props.onClose}
                                    disabled={props.isLoading}
                                >
                                    Cancelar
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return props.isOpen ? ReactDOM.createPortal(component, container) : null;
};
